import React, { useState, useEffect, useCallback } from 'react';
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Line,
  ComposedChart,
} from 'recharts';
import { Card, CardContent } from '../components/ui/Card';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { EmptyState } from '../components/ui/EmptyState';
import { PeriodSelector } from '../components/ui/PeriodSelector';
import { getCashflow, getCashflowTransactions, CashflowEntry, CashflowTransaction } from '../services/apiService';
import { formatBRL } from '../utils/FormatDecimal';
import { getMonthRange } from '../hooks/dashboardUtils';
import { X } from 'lucide-react';

interface DrilldownParams {
  year: number;
  month: number;
  label: string;
  category: string;
  direction: string;
  categoryLabel: string;
  directionLabel: string;
}

export default function CashFlowPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState<CashflowEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Drilldown modal state
  const [drilldown, setDrilldown] = useState<DrilldownParams | null>(null);
  const [drilldownData, setDrilldownData] = useState<CashflowTransaction[] | null>(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { end } = getMonthRange(year, month);
      const beginYear = year - 1;
      const begin = `${beginYear}-${String(month).padStart(2, '0')}-01`;
      const result = await getCashflow({ begin, end });
      setData(result.months);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao carregar fluxo de caixa');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openDrilldown = useCallback(async (params: DrilldownParams) => {
    setDrilldown(params);
    setDrilldownData(null);
    setDrilldownLoading(true);
    try {
      const result = await getCashflowTransactions({
        year: params.year,
        month: params.month,
        category: params.category,
        direction: params.direction,
      });
      setDrilldownData(result.transactions);
    } catch {
      setDrilldownData([]);
    } finally {
      setDrilldownLoading(false);
    }
  }, []);

  if (loading && !data) return <LoadingSpinner message="Carregando fluxo de caixa..." />;
  if (error) return <EmptyState title="Erro" description={error} />;
  if (!data || data.length === 0) return <EmptyState title="Sem dados" description="Nenhum dado disponível" />;

  const hasFin = data.some(r => r.entradas_financiamento > 0 || r.saidas_financiamento > 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <PeriodSelector year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} />
        {loading && <span className="text-xs text-gray-400">Atualizando...</span>}
      </div>

      {/* Chart */}
      <Card>
        <CardContent>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Fluxo de Caixa
          </h3>
          <ResponsiveContainer width="100%" height={350}>
            <ComposedChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="bars" tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <YAxis yAxisId="line" orientation="right" tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value: any) => formatBRL(Number(value))} />
              <Legend />
              <Bar yAxisId="bars" dataKey="entradas_operacional" name="Entradas Operacional" fill="#22c55e" stackId="entradas" />
              {hasFin && <Bar yAxisId="bars" dataKey="entradas_financiamento" name="Entradas Financiamento" fill="#6366f1" stackId="entradas" />}
              <Bar yAxisId="bars" dataKey="saidas_operacional" name="Saídas Operacional" fill="#ef4444" stackId="saidas" />
              {hasFin && <Bar yAxisId="bars" dataKey="saidas_financiamento" name="Saídas Financiamento" fill="#f59e0b" stackId="saidas" />}
              <Line yAxisId="line" type="monotone" dataKey="saldo_acumulado" name="Saldo Acumulado" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Detalhamento Mensal
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-3 font-medium text-gray-500" rowSpan={2}>Mês</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-600 bg-emerald-50 border-b border-emerald-100" colSpan={3}>Operacional</th>
                  {hasFin && <th className="text-center py-2 px-3 font-medium text-gray-600 bg-indigo-50 border-b border-indigo-100" colSpan={3}>Financiamento</th>}
                  <th className="text-center py-2 px-3 font-medium text-gray-600 bg-slate-100 border-b border-slate-200" colSpan={3}>Total</th>
                  <th className="text-right py-3 px-3 font-medium text-blue-600" rowSpan={2}>Saldo Acum.</th>
                </tr>
                <tr className="border-b border-gray-200">
                  <th className="text-right py-2 px-3 font-medium text-green-600 text-xs bg-emerald-50">Entradas</th>
                  <th className="text-right py-2 px-3 font-medium text-red-600 text-xs bg-emerald-50">Saídas</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-700 text-xs bg-emerald-50 border-l border-emerald-200">Resultado</th>
                  {hasFin && <th className="text-right py-2 px-3 font-medium text-indigo-600 text-xs bg-indigo-50">Entradas</th>}
                  {hasFin && <th className="text-right py-2 px-3 font-medium text-amber-600 text-xs bg-indigo-50">Saídas</th>}
                  {hasFin && <th className="text-right py-2 px-3 font-medium text-gray-700 text-xs bg-indigo-50 border-l border-indigo-200">Resultado</th>}
                  <th className="text-right py-2 px-3 font-medium text-green-700 text-xs bg-slate-100">Entradas</th>
                  <th className="text-right py-2 px-3 font-medium text-red-700 text-xs bg-slate-100">Saídas</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-700 text-xs bg-slate-100 border-l border-slate-300">Resultado</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => {
                  const stripe = i % 2 === 1;
                  const click = (category: string, direction: string, categoryLabel: string, directionLabel: string) => () => {
                    openDrilldown({
                      year: row.year, month: row.month, label: row.label,
                      category, direction, categoryLabel, directionLabel,
                    });
                  };
                  const clickable = "cursor-pointer hover:underline decoration-dotted";
                  const resultOp = row.entradas_operacional - row.saidas_operacional;
                  const resultFin = row.entradas_financiamento - row.saidas_financiamento;
                  const resultTotal = row.entradas - row.saidas;
                  const resultColor = (v: number) => v >= 0 ? 'text-green-700' : 'text-red-700';
                  return (
                    <tr key={`${row.year}-${row.month}`} className="border-b border-gray-100 hover:bg-blue-50/50">
                      <td className={`py-3 px-3 text-gray-700 font-medium ${stripe ? 'bg-gray-50' : ''}`}>{row.label}</td>
                      <td className={`py-3 px-3 text-right font-mono ${stripe ? 'bg-emerald-50/60' : 'bg-emerald-50/30'}`}>
                        <span className={`text-green-600 ${clickable}`} onClick={click('operacional', 'entradas', 'Operacional', 'Entradas')}>
                          {formatBRL(row.entradas_operacional)}
                        </span>
                      </td>
                      <td className={`py-3 px-3 text-right font-mono ${stripe ? 'bg-emerald-50/60' : 'bg-emerald-50/30'}`}>
                        <span className={`text-red-600 ${clickable}`} onClick={click('operacional', 'saidas', 'Operacional', 'Saídas')}>
                          {formatBRL(row.saidas_operacional)}
                        </span>
                      </td>
                      <td className={`py-3 px-3 text-right font-mono font-medium border-l border-emerald-200 ${resultColor(resultOp)} ${stripe ? 'bg-emerald-50/60' : 'bg-emerald-50/30'}`}>
                        {formatBRL(resultOp)}
                      </td>
                      {hasFin && (
                        <td className={`py-3 px-3 text-right font-mono ${stripe ? 'bg-indigo-50/60' : 'bg-indigo-50/30'}`}>
                          <span className={`text-indigo-600 ${clickable}`} onClick={click('financiamento', 'entradas', 'Financiamento', 'Entradas')}>
                            {formatBRL(row.entradas_financiamento)}
                          </span>
                        </td>
                      )}
                      {hasFin && (
                        <td className={`py-3 px-3 text-right font-mono ${stripe ? 'bg-indigo-50/60' : 'bg-indigo-50/30'}`}>
                          <span className={`text-amber-600 ${clickable}`} onClick={click('financiamento', 'saidas', 'Financiamento', 'Saídas')}>
                            {formatBRL(row.saidas_financiamento)}
                          </span>
                        </td>
                      )}
                      {hasFin && (
                        <td className={`py-3 px-3 text-right font-mono font-medium border-l border-indigo-200 ${resultColor(resultFin)} ${stripe ? 'bg-indigo-50/60' : 'bg-indigo-50/30'}`}>
                          {formatBRL(resultFin)}
                        </td>
                      )}
                      <td className={`py-3 px-3 text-right font-mono font-medium ${stripe ? 'bg-slate-100/80' : 'bg-slate-50/50'}`}>
                        <span className={`text-green-700 ${clickable}`} onClick={click('total', 'entradas', 'Total', 'Entradas')}>
                          {formatBRL(row.entradas)}
                        </span>
                      </td>
                      <td className={`py-3 px-3 text-right font-mono font-medium ${stripe ? 'bg-slate-100/80' : 'bg-slate-50/50'}`}>
                        <span className={`text-red-700 ${clickable}`} onClick={click('total', 'saidas', 'Total', 'Saídas')}>
                          {formatBRL(row.saidas)}
                        </span>
                      </td>
                      <td className={`py-3 px-3 text-right font-mono font-bold border-l border-slate-300 ${resultColor(resultTotal)} ${stripe ? 'bg-slate-100/80' : 'bg-slate-50/50'}`}>
                        {formatBRL(resultTotal)}
                      </td>
                      <td className={`py-3 px-3 text-right font-mono font-medium ${row.saldo_acumulado >= 0 ? 'text-blue-600' : 'text-orange-600'} ${stripe ? 'bg-gray-50' : ''}`}>
                        {formatBRL(row.saldo_acumulado)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Drilldown Modal */}
      {drilldown && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDrilldown(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  {drilldown.directionLabel} — {drilldown.categoryLabel}
                </h3>
                <p className="text-sm text-gray-500">{drilldown.label}</p>
              </div>
              <button onClick={() => setDrilldown(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {drilldownLoading ? (
                <div className="text-center py-8 text-gray-400">Carregando...</div>
              ) : !drilldownData || drilldownData.length === 0 ? (
                <div className="text-center py-8 text-gray-400">Nenhuma transação</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-medium text-gray-500">Data</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-500">Descrição</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-500">Contra-contas</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-500">Valor</th>
                      <th className="text-right py-2 px-3 font-medium text-blue-600">Saldo Parcial</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drilldownData.map((txn, i) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-blue-50 even:bg-gray-50">
                        <td className="py-2 px-3 text-gray-600 whitespace-nowrap">{txn.date}</td>
                        <td className="py-2 px-3 text-gray-800">{txn.payee}</td>
                        <td className="py-2 px-3 text-gray-500 text-xs">{txn.contras.join(', ')}</td>
                        <td className="py-2 px-3 text-right font-mono text-gray-800">{formatBRL(txn.amount)}</td>
                        <td className="py-2 px-3 text-right font-mono text-blue-600 font-medium">{formatBRL(txn.running_balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
